import { captureLogger } from '../../configure/logger-facade';
import { type CaptureLogFn, type CaptureLogger } from '../../ports/logger';
import { getLogCohort } from './logCohort';

// Merges the rollout cohort into object payloads, read per call because the mode
// is pinned long after this module evaluates. A `child()` binding would rely on
// the host re-reading bindings per record, which the contract does not promise.
// Errors and non-objects pass through so the host keeps its stack-capturing branch.
const withCohort =
  (log: CaptureLogFn): CaptureLogFn =>
  (...args: unknown[]): void => {
    const cohort = getLogCohort();
    const [payload, ...rest] = args;
    if (
      cohort === undefined ||
      payload === null ||
      typeof payload !== 'object' ||
      payload instanceof Error
    ) {
      log(...args);
      return;
    }
    log({ ...(payload as Record<string, unknown>), pipeline_mode: cohort }, ...rest);
  };

const tagged = (base: CaptureLogger): CaptureLogger => ({
  trace: withCohort(base.trace),
  debug: withCohort(base.debug),
  info: withCohort(base.info),
  warn: withCohort(base.warn),
  error: withCohort(base.error),
  fatal: withCohort(base.fatal),
  log: withCohort(base.log),
  // Nothing derives a child today; wrapping keeps the cohort on one that does.
  child: (bindings) => tagged(base.child(bindings)),
});

export const logger = tagged(captureLogger.child({ module: 'audio-recording' }));
